import { AppIcon, type IconDefinition } from '@/components/ui/app-icon';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { forwardRef, type ReactNode } from 'react';
import { SidebarLink } from './sidebar-link.component';
import { SIDEBAR_HOVER, SIDEBAR_ICON } from './tabs-sidebar.styles';

type SidebarIconButtonProps = {
  /** The accessible name, and the tooltip. */
  label: string;
  icon: IconDefinition;
  onClick?: () => void;
  /** Renders a link instead of a button. */
  to?: string;
  /**
   * The link's page is on screen: raised and marked current, as the sidebar
   * draws the row for the page on screen. Only this marks the link current,
   * not the router matching its path.
   */
  active?: boolean;
  disabled?: boolean;
  /** Tooltip text when it differs from the label. */
  tooltip?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Size classes; 28px square by default. */
  className?: string;
  /** The glyph's size: 15px, or 16 as a cell of the page rail. */
  iconSize?: number;
  /** Dots and badges over the icon. */
  children?: ReactNode;
};

/** A square icon control on the sidebar, with its label as a tooltip. */
export function SidebarIconButton({
  label,
  icon,
  onClick,
  to,
  active = false,
  disabled = false,
  tooltip,
  side = 'right',
  className,
  iconSize = 15,
  children,
}: SidebarIconButtonProps) {
  const classes = cn(
    'relative grid h-7 w-7 shrink-0 place-items-center rounded-md transition-colors',
    'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50',
    SIDEBAR_ICON,
    // The current page is marked for assistive tech only: Lev wants the gear
    // to keep its plain look on Settings, not the raised card of a row.
    disabled ? 'cursor-not-allowed opacity-40' : SIDEBAR_HOVER,
    className
  );
  const glyph = <AppIcon icon={icon} style={{ fontSize: iconSize }} />;
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        {to && !disabled ? (
          <SidebarLink
            to={to}
            current={active}
            aria-label={label}
            className={classes}
          >
            {glyph}
            {children}
          </SidebarLink>
        ) : (
          <IconButton
            label={label}
            disabled={disabled}
            onClick={onClick}
            className={classes}
          >
            {glyph}
            {children}
          </IconButton>
        )}
      </TooltipTrigger>
      <TooltipContent side={side} sideOffset={6}>
        {tooltip ?? label}
      </TooltipContent>
    </Tooltip>
  );
}

// aria-disabled rather than disabled, so the tooltip still shows on hover.
const IconButton = forwardRef<
  HTMLButtonElement,
  {
    label: string;
    disabled: boolean;
    onClick?: () => void;
    className: string;
    children: ReactNode;
  }
>(function IconButton(
  { label, disabled, onClick, className, children, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type='button'
      aria-label={label}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      className={className}
      {...rest}
    >
      {children}
    </button>
  );
});
